import type { IFilterableListItem } from '@sixui/ui';
import { useId } from 'react';
import {
  faChevronLeft,
  faChevronRight,
  faChevronsLeft,
  faChevronsRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Select, Stack } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';

import type { IContainerProps } from '~/components/utils/Container';

export type ITablePaginationActionType = 'first' | 'last' | 'next' | 'previous';

export type ITablePaginationLabelDisplayedRowsArgs = {
  from: number;
  to: number;
  total: number;
  page: number;
};

export type ITablePaginationProps = IContainerProps & {
  align?: 'left' | 'right';

  /**
   * The total number of rows.
   */
  count: number;

  /**
   * Callback fired when the page is changed.
   *
   * @param event - The event source of the callback.
   * @param page - The page selected.
   */
  onPageChange?: (event: React.MouseEvent | null, page: number) => void;

  /**
   * If `true`, the component is disabled.
   */
  disabled?: boolean;

  i18n?: {
    /**
     * Accepts a function which returns a string value that provides a
     * user-friendly name for the current page. This is important for screen
     * reader users.
     *
     * * @param type - The link or button type to format.
     */
    goTo: (type: ITablePaginationActionType) => string;

    /**
     * Customize the displayed rows label. Invoked with a
     * `{ from, to, count, page }` object.
     */
    displayedRows: (
      paginationInfo: ITablePaginationLabelDisplayedRowsArgs,
    ) => React.ReactNode;

    /**
     * Customize the rows per page label.
     */
    rowsPerPage: React.ReactNode;
  };

  /**
   * Callback fired when the number of rows per page is changed.
   *
   * @param rowsPerPage - The new number of rows per page.
   */
  onRowsPerPageChange?: (rowsPerPage: number) => void;

  /**
   * The zero-based index of the current page.
   */
  page: number;

  /**
   * The number of rows per page.
   */
  rowsPerPage: number;

  /**
   * Customizes the options of the rows per page select field. If less than two
   * options are available, no select field will be displayed.
   *
   * @defaultValue [10, 25, 50, 100]
   */
  rowsPerPageOptions?: ReadonlyArray<number | { value: number; label: string }>;

  /**
   * If `true`, show the first-page button.
   */
  showFirstButton?: boolean;

  /**
   * If `true`, show the last-page button.
   */
  showLastButton?: boolean;

  children?: React.ReactNode;
};

const styles = stylex.create({
  host: {
    position: 'relative',
    minHeight: 52,
    color: colorSchemeTokens.onSurface,
  },
  host$alignRight: {
    flexDirection: 'row-reverse',
  },
  pageSelectorLabel: {
    flexShrink: 0,
  },
  rows: {
    flexShrink: 0,
  },
});

export const TablePagination: React.FC<ITablePaginationProps> = (props) => {
  const {
    sx,
    align = 'left',
    count,
    onPageChange,
    disabled,
    i18n = {
      goTo: (type) => `Go to ${type} page`,
      displayedRows: ({ from, to, total }) =>
        `${from}–${to} of ${total < 0 ? total : `more than ${to}`}`,
      rowsPerPage: 'Rows per page:',
    },
    onRowsPerPageChange,
    page,
    rowsPerPage,
    rowsPerPageOptions = [10, 25, 50, 100],
    showFirstButton,
    showLastButton,
    children,
  } = props;

  const labelId = useId();

  const isFirstPage = page === 0;
  const hasPreviousPage = page === 0;
  const hasNextPage =
    count !== -1 ? page >= Math.ceil(count / rowsPerPage) - 1 : false;
  const isLastPage = page >= Math.ceil(count / rowsPerPage) - 1;

  const getLabelDisplayedRowsTo = (): number => {
    if (count === -1) {
      return (page + 1) * rowsPerPage;
    }

    return rowsPerPage === -1
      ? count
      : Math.min(count, (page + 1) * rowsPerPage);
  };

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ): void => onPageChange?.(event, 0);

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => void onPageChange?.(event, page - 1);

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ): void => {
    onPageChange?.(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) =>
    void onPageChange?.(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));

  const pageItems: Array<IFilterableListItem> | undefined =
    rowsPerPageOptions.length > 1
      ? rowsPerPageOptions.map((rowsPerPageOption) => {
          const value = String(
            typeof rowsPerPageOption === 'number'
              ? rowsPerPageOption
              : rowsPerPageOption.value,
          );
          const label =
            typeof rowsPerPageOption === 'number'
              ? String(rowsPerPageOption)
              : rowsPerPageOption.label;

          return {
            value,
            label,
          };
        })
      : undefined;

  return (
    <Stack
      horizontal
      gap={6}
      sx={[styles.host, align === 'right' && styles.host$alignRight, sx]}
    >
      <Stack horizontal gap={2}>
        {showFirstButton && (
          <IconButton
            onClick={handleFirstPageButtonClick}
            disabled={disabled || isFirstPage}
            aria-label={i18n.goTo('first')}
            title={i18n.goTo('first')}
            icon={<FontAwesomeIcon icon={faChevronsLeft} />}
          />
        )}

        <IconButton
          onClick={handleBackButtonClick}
          disabled={disabled || hasPreviousPage}
          color="inherit"
          aria-label={i18n.goTo('previous')}
          title={i18n.goTo('previous')}
          icon={<FontAwesomeIcon icon={faChevronLeft} />}
        />

        <IconButton
          onClick={handleNextButtonClick}
          disabled={disabled || hasNextPage}
          color="inherit"
          aria-label={i18n.goTo('next')}
          title={i18n.goTo('next')}
          icon={<FontAwesomeIcon icon={faChevronRight} />}
        />

        {showLastButton && (
          <IconButton
            onClick={handleLastPageButtonClick}
            disabled={disabled || isLastPage}
            aria-label={i18n.goTo('last')}
            title={i18n.goTo('last')}
            icon={<FontAwesomeIcon icon={faChevronsRight} />}
          />
        )}
      </Stack>

      <p {...stylex.props(styles.rows)}>
        {i18n.displayedRows({
          from: count === 0 ? 0 : page * rowsPerPage + 1,
          to: getLabelDisplayedRowsTo(),
          total: count < 0 ? -1 : count,
          page,
        })}
      </p>

      {pageItems ? (
        <Stack horizontal gap={2}>
          <p {...stylex.props(styles.pageSelectorLabel)} id={labelId}>
            {i18n.rowsPerPage}
          </p>
          <Select
            variant="outlined"
            value={String(rowsPerPage)}
            onChange={(value) => onRowsPerPageChange?.(Number(value))}
            aria-label={labelId}
            disabled={disabled}
            items={pageItems}
          />
        </Stack>
      ) : null}

      {children}
    </Stack>
  );
};
